import { ChannelNames, EventName, IteroEventBus } from '@itero/itero-client-communication';
import { TimberService } from '@logging/timber.service';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class CommunicationService {
	constructor(
		private readonly eventBus: IteroEventBus,
		private readonly timberService: TimberService
	) {}

	async publishEvent(
		channelName: ChannelNames,
		publisherAppId: string,
		eventName: string,
		targetApplications: Array<string>,
		eventPayload?: unknown
	): Promise<boolean> {
		if (!this.canPublishEvent(eventName as EventName)) {
			this.timberService.error(
				`Failed to publish event ${eventName} to channel ${channelName} by application ${publisherAppId}, ` +
					'missing region or session id.',
				{ module: 'CommunicationService' }
			);

			return false;
		}

		try {
			const result = await this.eventBus.send({
				publisherAppId,
				eventName,
				targetApplications,
				eventPayload,
			});

			if (!result.success) {
				this.timberService.error(
					`Failed to publish event ${eventName} to channel ${channelName} by application ${publisherAppId}`,
					{
						module: 'CommunicationService',
					}
				);

				return false;
			}

			this.timberService.info(
				`Event ${eventName} published successfully by application ${publisherAppId} to channel ${channelName}`,
				{
					module: 'CommunicationService',
				}
			);

			return true;
		} catch (err) {
			this.timberService.error(JSON.stringify(err), { module: 'CommunicationService' });

			return false;
		}
	}

	subscribeToChannel(channelName: ChannelNames, callbackAppId: string, callbackFunc: (...args: unknown[]) => void): boolean {
		const subscribedSuccessfully = this.eventBus.subscribe({
			channelName,
			callbackAppId,
			callbackFunc,
		}).success;

		if (!subscribedSuccessfully) {
			this.timberService.error(`Application ${callbackAppId} failed to subscribe to channel ${channelName}`, {
				module: 'CommunicationService',
			});
		} else {
			this.timberService.info(`Application ${callbackAppId} subscribed successfully to channel ${channelName}`, {
				module: 'CommunicationService',
			});
		}

		return subscribedSuccessfully;
	}

	unsubscribeFromChannel(channelName: ChannelNames, appIdToUnsubscribe: string): void {
		this.eventBus.unsubscribe(channelName, appIdToUnsubscribe);
	}

	setRegion(region: string) {
		this.eventBus.setRegion(region);
	}

	setSessionInfo(sessionId: string) {
		this.eventBus.setSessionId(sessionId);
	}

	private canPublishEvent(eventName: EventName): boolean {
		return (
			eventName === EventName.initContext ||
			(this.eventBus.getRegion() !== undefined &&
				this.eventBus.getRegion() !== '' &&
				this.eventBus.getSessionId() !== undefined &&
				this.eventBus.getSessionId() !== '')
		);
	}
}
